import { React, useState, useEffect, useContext } from "react";
import CartTable from "./CartTable";
import CartHeader from "./CartHeader";
import CartSummary from "./CartSummary";
import HoldBillDialog from "./HoldBillDialog";
import OpenHoldsPanel from "./OpenHoldsPanel";
import KOTDialog from "./KOTDialog";
import useStyles from "./CartStyles";
import { StaffCommissionDetailContext } from "../Context/StaffCommissionDetailsContext";
import { useCart } from "../Context/CartContext";
import { useOrderSession, getCartItemsFromSession } from "../Context/OrderSessionContext";
import { useSnackbar } from "react-simple-snackbar";
import { usePayNow } from "../Context/PayNowContext";
import { useDiscount } from "../Context/DiscountContext";
import { useTheme } from "@material-ui/core/styles";
import { Button, Drawer, Divider, CircularProgress } from "@material-ui/core";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import usePermissions from "../utils/permissions";

const successOptions = {
  position: "top-center",
  style: {
    backgroundColor: "#4caf50",
    color: "white",
    fontFamily: "Arial, sans-serif",
    fontSize: "16px",
    textAlign: "center",
    padding: "12px 24px",
    borderRadius: "8px",
    zIndex: 10004,
  },
  closeStyle: {
    color: "white",
    fontSize: "14px",
  },
};

const errorOptions = {
  position: "top-center",
  style: {
    backgroundColor: "#f44336",
    color: "white",
    fontFamily: "Arial, sans-serif",
    fontSize: "16px",
    textAlign: "center",
    padding: "12px 24px",
    borderRadius: "8px",
    zIndex: 10004,
  },
  closeStyle: {
    color: "white",
    fontSize: "14px",
  },
};

function Cart({ open, handleDrawerClose, currency, invoice, invoices }) {
  const classes = useStyles();
  const {
    staffCommissionDetail,
    setStaffCommissionDetail,
    staffCommissionDetailsArray,
    setStaffCommissionDetailsArray,
  } = useContext(StaffCommissionDetailContext);
  const theme = useTheme();
  const { cartItems, setCartItems, setVat } = useCart();
  const { openPayNow, handlePayNow } = usePayNow();
  const { setDiscountVal, setDiscountFlag } = useDiscount();
  const {
    activeSessionId,
    activeSession,
    openSessions,
    createSession,
    clearActiveSession,
    fetchOpenSessions,
    addItemToSession,
    refreshActiveSession,
  } = useOrderSession();
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [openHoldsOpen, setOpenHoldsOpen] = useState(false);
  const [kotDialogOpen, setKotDialogOpen] = useState(false);
  const [kotButtonLoading, setKotButtonLoading] = useState(false);
  const [openSuccessSnackbar] = useSnackbar(successOptions);
  const [openErrorSnackbar] = useSnackbar(errorOptions);
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const holdFeatureEnabled = !!profile?.userProfile?.enableHoldKot;
  

  // Sync cart from session when in hold mode so CartSummary and totals stay correct
  useEffect(() => {
    if (activeSessionId && activeSession?.items) {
      setCartItems(getCartItemsFromSession(activeSession));
    }
  }, [activeSessionId, activeSession]);

  const {
    loading: permissionsLoading,
    canUpdatePage,
    canDeletePage,
    canCreatePage,
    isAdmin,
  } = usePermissions();

  useEffect(() => {
    if (invoice && !activeSessionId) {
      const mappedItems = invoice.items.map(
        ({ item, quantity, rate, originalRate, priceAdjusted, remark }) => {
          const baseRate =
            originalRate ??
            item?.units?.sale?.price ??
            rate ??
            item?.rate ??
            0;
          const effectiveRate = rate ?? baseRate;
          const adjusted =
            priceAdjusted ??
            Math.abs((effectiveRate || 0) - (baseRate || 0)) > 0.0001;
          return {
            ...item,
            quantity,
            originalPrice: baseRate,
            overridePrice: adjusted ? effectiveRate : null,
            priceAdjusted: adjusted,
            remark: remark ?? null,
          };
        }
      );
      setCartItems(mappedItems);
      invoice?.discount ? setDiscountFlag(true) : setDiscountFlag(false);
      setDiscountVal(invoice.discount);
      setVat(invoice.vat);
    }
  }, [invoice, activeSessionId]);

  // Fetch open sessions count on mount
  useEffect(() => {
    if (holdFeatureEnabled) {
      fetchOpenSessions();
    }
  }, [fetchOpenSessions, holdFeatureEnabled]);

  useEffect(() => {
    if (!holdFeatureEnabled) {
      setHoldDialogOpen(false);
      setOpenHoldsOpen(false);
    }
  }, [holdFeatureEnabled]);

  const handleHoldBill = async (label) => {
    if (!holdFeatureEnabled) return;
    if (!cartItems.length) return;
    try {
      const session = await createSession(label);
      // Add current cart items to the new session
      for (const item of cartItems) {
        try {
          await addItemToSession(session._id, {
            ...item,
            quantity: item.quantity ?? 1,
          });
        } catch (e) {
          console.error("Failed to add item to session:", e);
        }
      }
      setDiscountFlag(false);
      setDiscountVal(0);
      // Keep session active so KOT button and editing UI show; refresh to get items
      await refreshActiveSession();
      fetchOpenSessions();
      setHoldDialogOpen(false);
      openSuccessSnackbar(`Bill held as "${label}"`);
    } catch (err) {
      openErrorSnackbar(err?.response?.data?.message || "Failed to hold bill");
    }
  };

  const handleNewBill = () => {
    const prevLabel = activeSession?.label;
    clearActiveSession();
    setCartItems([]);
    setDiscountFlag(false);
    setDiscountVal(0);
    if (prevLabel) {
      openSuccessSnackbar(`Saved & exited: ${prevLabel}`);
    }
  };

  const handleOpenKOT = async () => {
    if (!holdFeatureEnabled) return;
    if (!activeSessionId || !activeSession) return;
    if (!activeSession?.items || activeSession.items.length === 0) return;
    setKotButtonLoading(true);
    try {
      const fresh = await refreshActiveSession();
      setKotDialogOpen(true);
    } finally {
      setTimeout(() => setKotButtonLoading(false), 300);
    }
  };

  const getUnprintedCount = () => {
    if (!activeSession) return 0;
    return (activeSession.items || []).filter((item) => !item.kotPrinted).length;
  };

  const isSessionMode = Boolean(activeSessionId);
  const canProceed = isAdmin() || canCreatePage("Invoice");

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <CartHeader
        classes={classes}
        theme={theme}
        handleDrawerClose={handleDrawerClose}
        invoices={invoices}
      />

      <Divider />

      <div style={{ padding: "6px 10px", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {holdFeatureEnabled ? (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setHoldDialogOpen(true)}
              disabled={!canProceed || permissionsLoading || cartItems.length === 0 || isSessionMode}
              style={{ flex: 1, minWidth: 70, minHeight: 32 }}
              title={
                isSessionMode
                  ? "Already in a hold - use New Bill first"
                  : cartItems.length === 0
                  ? "Add items to cart first"
                  : "Hold this bill"
              }
            >
              + Hold
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setOpenHoldsOpen(true)}
              disabled={!canProceed || permissionsLoading}
              style={{ flex: 1, minWidth: 70, minHeight: 32 }}
            >
              Holds ({openSessions?.length || 0})
            </Button>
          </>
        ) : null}
        {isSessionMode && activeSession && (
          <>
            <div style={{ flex: "0 0 100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 2 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#ef7f1a" }}>
                {activeSession.label}
              </span>
              <span style={{ fontSize: 10, color: "#888" }}>Auto-saved</span>
            </div>
            {holdFeatureEnabled && (
              <Button
                size="small"
                variant="contained"
                startIcon={kotButtonLoading ? <CircularProgress size={14} style={{ color: "white" }} /> : <RestaurantIcon style={{ fontSize: 18 }} />}
                onClick={handleOpenKOT}
                disabled={!activeSessionId || !activeSession || kotButtonLoading}
                style={{
                  flex: "1 1 auto",
                  minHeight: 32,
                  backgroundColor: kotButtonLoading ? "#ffb74d" : "#ff9800",
                  color: "white",
                  cursor: activeSessionId && activeSession && !kotButtonLoading ? "pointer" : "not-allowed",
                  transition: "background-color 0.2s",
                }}
                title={activeSessionId && activeSession ? "Open KOT dialog" : "No active session"}
              >
                {kotButtonLoading ? "..." : `KOT${getUnprintedCount() > 0 ? ` (${getUnprintedCount()})` : ""}`}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={handleNewBill}
              style={{ flex: "1 1 auto", minHeight: 32 }}
            >
              ← New Bill
            </Button>
          </>
        )}
      </div>

      {holdFeatureEnabled && (
        <>
          <HoldBillDialog
            open={holdDialogOpen}
            onClose={() => setHoldDialogOpen(false)}
            onCreate={handleHoldBill}
          />
          <OpenHoldsPanel
            open={openHoldsOpen}
            onClose={() => setOpenHoldsOpen(false)}
            onLoadSuccess={(label) => openSuccessSnackbar(`Loaded hold: ${label}`)}
            onLoadError={(msg) => openErrorSnackbar(msg || "Failed to load hold")}
          />
          <KOTDialog
            open={kotDialogOpen}
            onClose={() => setKotDialogOpen(false)}
            session={activeSession}
            refreshSession={refreshActiveSession}
            onSuccess={async (msg) => {
              openSuccessSnackbar(msg);
              await refreshActiveSession?.();
              fetchOpenSessions();
            }}
            onError={(msg) => openErrorSnackbar(msg)}
          />
        </>
      )}

      <Divider />

      <CartTable currency={currency} />

      <Divider />
      <CartSummary currency={currency} />
      <Divider />

      <Button
        variant="contained"
        color="primary"
        onClick={handlePayNow}
        className={classes.payNowButton}
        disabled={
          cartItems.length === 0 ||
          openPayNow ||
          permissionsLoading ||
          !canProceed
        }
        style={{
          cursor: permissionsLoading || !canProceed ? "not-allowed" : "pointer",
        }}
      >
        {isSessionMode ? "Final Checkout" : "Save and Continue"}
      </Button>
    </Drawer>
  );
}
export default Cart;
